// ============================================================================
// CopyText — the activity page's click-to-copy (round 10, 2026-08-13)
//
// Replaces the wallet's InlineCopyableText / CopyableMember here. Those
// components carry their own padding, negative margins, width:100% wrappers,
// icons and portal popouts — on this page they truncated the average values
// to "0 / $… ", clipped the last letter of member names, crowded rows with
// icons, overflowed long ids out of their popout, and their expanded
// hit-areas even added a phantom couple-of-pixels scroll to the events panel.
//
// Owner directive: "that icon can be removed into a simple click with copied
// is sufficient, and it shouldn't change much when the user clicks it."
// So: renders its children as PLAIN TEXT — inherits font, size, and color
// exactly, adds zero padding/margins — with a quiet dotted underline on
// hover, and on click copies `copyValue` (or the text content) and floats a
// tiny "copied" pill over the text for a beat. No layout shift, ever.
// stopPropagation, so copying never expands a row or drills a member.
// ============================================================================

import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { copyToClipboard } from '../common/CopyableValue/utils';
import { ACTIVITY_COLORS } from './activityTheme';

const Wrap = styled.span`
  position: relative;
  display: inline;
  cursor: copy;

  &:hover {
    text-decoration: underline dotted rgba(198, 167, 94, 0.75);
    text-underline-offset: 3px;
  }

  &:focus-visible {
    outline: 2px solid rgba(198, 167, 94, 0.5);
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const CopiedPill = styled.span`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  background: ${ACTIVITY_COLORS.ink};
  color: #fff;
  font-size: 0.625rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  line-height: 1;
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
  pointer-events: none;
  white-space: nowrap;
  z-index: 4;
`;

interface Props {
  /** What lands on the clipboard. Defaults to the rendered text content. */
  copyValue?: string;
  children: React.ReactNode;
  className?: string;
}

const CopyText: React.FC<Props> = ({ copyValue, children, className }) => {
  const [copied, setCopied] = useState(false);
  const ref = useRef<HTMLSpanElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const doCopy = useCallback(async (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    const value = copyValue ?? ref.current?.textContent ?? '';
    if (!value) return;
    const ok = await copyToClipboard(value);
    if (ok) {
      setCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 1100);
    }
  }, [copyValue]);

  return (
    <Wrap
      ref={ref}
      className={className}
      role="button"
      tabIndex={0}
      title="click to copy"
      aria-label="click to copy"
      onClick={doCopy}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          void doCopy(e);
        }
      }}
    >
      {children}
      {copied && <CopiedPill aria-hidden>copied</CopiedPill>}
    </Wrap>
  );
};

export default CopyText;
